import React, { useEffect, useState } from 'react';
import { ArrowRight, Loader2, Target } from 'lucide-react';
import { FieldError, inputClass, profileCard } from './ProfileUI';

const RoleRecommendations = ({
  recommendations,
  sourceSkills,
  selectedRoleId,
  customRole,
  finalRoleTitle,
  finalRoleType,
  customRoleFramework,
  onSelectRole,
  onSelectCustomRole,
  onDeselectRole,
  onProceed,
  isLoading,
  isSelectingRole,
  proceedLabel = 'Continue',
}) => {
  const [customRoleInput, setCustomRoleInput] = useState(customRole || '');
  const [customRoleError, setCustomRoleError] = useState('');

  useEffect(() => {
    setCustomRoleInput(customRole || '');
    setCustomRoleError('');
  }, [customRole]);

  const hasFinalRole = finalRoleType === 'recommended' || finalRoleType === 'custom';
  const hasSkills = Array.isArray(sourceSkills) && sourceSkills.length > 0;

  const handleCustomRoleSave = () => {
    const normalizedRole = customRoleInput.trim();
    if (!normalizedRole) {
      setCustomRoleError('Enter a custom role name before saving');
      return;
    }
    setCustomRoleError('');
    onSelectCustomRole(normalizedRole);
  };

  return (
    <section className={profileCard}>
      <div className="mb-5 flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-purple-300">Step 03</p>
          <h2 className="mt-2 text-2xl font-bold text-white">Role recommendations</h2>
          <p className="mt-2 text-sm leading-6 text-slate-300">
            Ranked from strongest skill match to weakest using your current structured skills.
          </p>
        </div>
        <div className="rounded-2xl border border-purple-400/20 bg-purple-500/10 p-3 text-purple-200">
          <Target className="h-6 w-6" />
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center gap-3 rounded-2xl border border-white/10 bg-slate-950/40 px-4 py-5 text-sm text-slate-300">
          <Loader2 className="h-4 w-4 animate-spin" />
          Generating role recommendations from your current skills
        </div>
      ) : null}

      {!isLoading && !hasSkills ? (
        <div className="rounded-2xl border border-white/10 bg-slate-950/40 px-4 py-5 text-sm text-slate-300">
          Add or upload skills to see recommended roles here.
        </div>
      ) : null}

      {!isLoading && hasSkills && recommendations.length ? (
        <div className="grid gap-4 xl:grid-cols-2">
          {recommendations.map((role) => {
            const isSelected = selectedRoleId === role.role_id;
            return (
              <article
                key={role.role_id}
                className={`rounded-2xl border p-5 transition ${
                  isSelected
                    ? 'border-purple-400/60 bg-purple-500/10 shadow-lg shadow-purple-500/10'
                    : 'border-slate-700/60 bg-slate-900/40'
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-semibold text-white">{role.role_title}</h3>
                    <p className="mt-2 text-sm leading-6 text-slate-300">{role.description}</p>
                  </div>
                  <div className="rounded-xl bg-slate-950/60 px-3 py-2 text-right">
                    <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Match</p>
                    <p className="text-xl font-bold text-purple-200">{role.match_percentage}%</p>
                  </div>
                </div>

                <div className="mt-4 space-y-3 text-sm">
                  <div>
                    <p className="font-semibold text-emerald-300">Matching skills</p>
                    <p className="mt-1 text-slate-300">
                      {role.matching_skills.length ? role.matching_skills.slice(0, 5).join(', ') : 'No strong overlaps yet'}
                    </p>
                  </div>
                  <div>
                    <p className="font-semibold text-amber-300">Missing key skills</p>
                    <p className="mt-1 text-slate-300">
                      {role.missing_skills.length ? role.missing_skills.slice(0, 5).join(', ') : 'You already cover the main skill gaps'}
                    </p>
                  </div>
                </div>

                <div className="mt-5 flex items-center justify-between gap-3">
                  <p className="text-xs leading-5 text-slate-400">{role.explanation}</p>
                  <div className="flex items-center gap-2">
                    {isSelected ? (
                      <>
                        <span className="rounded-lg bg-purple-600 px-4 py-2 text-sm font-semibold text-white">
                          Selected Role
                        </span>
                        <button
                          type="button"
                          onClick={onDeselectRole}
                          disabled={isSelectingRole}
                          className="rounded-lg border border-slate-600 px-4 py-2 text-sm font-semibold text-slate-200 transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {isSelectingRole ? 'Saving...' : 'Deselect Role'}
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        onClick={() => onSelectRole(role.role_id)}
                        disabled={isSelectingRole}
                        className="rounded-lg bg-slate-800 px-4 py-2 text-sm font-semibold text-slate-100 transition hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        Select Role
                      </button>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      ) : null}

      <div className="mt-5 rounded-2xl border border-slate-700/60 bg-slate-950/40 p-5">
        <div className="flex flex-col gap-2">
          <p className="text-sm font-semibold text-white">Add Custom Role</p>
          <p className="text-sm leading-6 text-slate-300">
            Override the recommended list with your own final target role.
          </p>
        </div>

        <div className="mt-4 flex flex-col gap-3 sm:flex-row">
          <input
            type="text"
            value={customRoleInput}
            maxLength={255}
            onChange={(event) => {
              setCustomRoleInput(event.target.value);
              if (customRoleError) {
                setCustomRoleError('');
              }
            }}
            placeholder="Enter a custom role"
            className={inputClass}
          />
          <button
            type="button"
            onClick={handleCustomRoleSave}
            disabled={isSelectingRole}
            className="rounded-lg bg-slate-800 px-4 py-3 text-sm font-semibold text-slate-100 transition hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isSelectingRole && finalRoleType !== 'recommended' ? 'Saving...' : 'Save Custom Role'}
          </button>
        </div>
        <FieldError>{customRoleError}</FieldError>
      </div>

      <div className="mt-5 rounded-2xl border border-purple-400/20 bg-purple-500/10 p-5">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-purple-200">Role Summary</p>
            <h3 className="mt-2 text-lg font-semibold text-white">
              {hasFinalRole ? finalRoleTitle : 'Choose a final role to continue'}
            </h3>
            <p className="mt-2 text-sm leading-6 text-purple-100/90">
              {hasFinalRole
                ? finalRoleType === 'custom'
                  ? 'Custom role is now your active final role and overrides any recommended selection.'
                  : 'Recommended role is your active final role.'
                : 'You need one final role decision before continuing to the next step.'}
            </p>
          </div>
          <div className="flex flex-col items-start gap-3 lg:items-end">
            {hasFinalRole ? (
              <button
                type="button"
                onClick={onDeselectRole}
                disabled={isSelectingRole}
                className="rounded-lg border border-slate-600 px-4 py-2 text-sm font-semibold text-slate-200 transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isSelectingRole ? 'Saving...' : 'Clear Selection'}
              </button>
            ) : null}
            <button
              type="button"
              onClick={onProceed}
              disabled={!hasFinalRole || isSelectingRole}
              className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 px-4 py-3 text-sm font-semibold text-white transition hover:from-purple-700 hover:to-pink-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {proceedLabel}
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>

        {finalRoleType === 'custom' && customRoleFramework ? (
          <div className="mt-4 grid gap-4 border-t border-purple-300/15 pt-4 md:grid-cols-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-purple-200">Baseline Skills</p>
              <p className="mt-2 text-sm leading-6 text-slate-200">
                {customRoleFramework.baseline_skills?.join(', ') || 'Core fundamentals'}
              </p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-purple-200">Focus Areas</p>
              <p className="mt-2 text-sm leading-6 text-slate-200">
                {customRoleFramework.focus_areas?.join(', ') || customRoleFramework.roadmap_target}
              </p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-purple-200">Roadmap Target</p>
              <p className="mt-2 text-sm leading-6 text-slate-200">{customRoleFramework.roadmap_target}</p>
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default RoleRecommendations;
